import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import addAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import alertIcon from "@insite/mobius/Icons/AlertCircle";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Tooltip from "@MasterBluePrint/MobiusOverrides/Tooltip/Tooltip";
import themeColors from "@MasterBluePrint/Styles/variables";
import parse from "html-react-parser";
import React, { ReactNode, RefObject, useContext, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    newsletterText = "newsletterText",
    showNewsletter = "showNewsletter",
    newsletterDefault = "newsletterDefault",
    newsletterCSS = "newsletterCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.newsletterText]: string;
        [fields.showNewsletter]: boolean;
        [fields.newsletterDefault]: boolean;
        [fields.newsletterCSS]: string;
    };
}

const mapDispatchToProps = {
    addAccount,
    signIn,
    validatePassword,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
};

const urlRegExp = new RegExp("(.*?)\.com");

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    returnUrl: getCreateAccountReturnUrl(state),
    siteUrl: urlRegExp.exec(state.context.website.uri),
});

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AddAccountParameter {
    userName: string;
    email: string;
    password: string;
    isSubscribed: boolean;
    properties: { [key: string]: string };
    onSuccess?: () => void;
    onError?: (error: string) => void;
}

export interface CreateAccountStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    userGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    userNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requirementsGridItem?: GridItemProps;
    requirementsTitle?: TypographyProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    validatePassword: InjectableCss;
    redUnderline?: InjectableCss;
    flexcolumn?: InjectableCss;
    forTitle?: InjectableCss;
    flexrow?: InjectableCss;
    greyUnderline?: InjectableCss;
    wFull?: InjectableCss;
    forCard?: InjectableCss;
    mr1?: InjectableCss;
    forSubtitle?: InjectableCss;
    mb3?: InjectableCss;
    mb6?: InjectableCss;
    blueLink?: InjectableCss;
    saveButton?: InjectableCss;
    aleft?: InjectableCss;
    wHalf?: InjectableCss;
    tooltip?: InjectableCss;
    phoneGridItem?: GridItemProps;
    extGridItem?: GridItemProps;
    cancelGridItem?: GridItemProps;
    cancelText?: InjectableCss;
    validationFailed?: InjectableCss;
    formStylesSpace?: InjectableCss;
    justifyBetween?: InjectableCss;
    showMobile?: InjectableCss;
    hideDesk?: InjectableCss;
    showsmall?: InjectableCss;
    hidebig?: InjectableCss;
    subscriptionCheckboxGroup?: CheckboxGroupComponentProps;
    subscriptionCheckbox?: CheckboxProps;
}

export const createAccountStyles: CreateAccountStyles = {
    mainGridContainer: {},
    centeringWrapper: {
        css: css`
            display: flex;
            height: 18.75rem;
            justify-content: center;
            align-items: center;
        `,
    },
    tooltip: {
        css: css`
            background-color: ${themeColors.colorToolTip};
            box-shadow: -1px 1px 5px 4px rgba(0, 0, 0, 0.04);
            -webkit-box-shadow: -1px 1px 5px 4px rgba(0, 0, 0, 0.04);
            -moz-box-shadow: -1px 1px 5px 4px rgba(0, 0, 0, 0.04);
            position: absolute;
            width: 500px;
            right: 0;
            @media (max-width: 767px) {
                display: none;
            }
        `,
    },
    userGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                             {
                                /* padding-left: 6.25rem; */
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    userGridContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: 100%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    emailGridItem: {
        width: 12,
    },
    userNameGridItem: {
        width: 12,
    },
    phoneGridItem: {
        width: 8,
        css: css`
            flex-direction: column;
        `,
    },
    extGridItem: {
        width: 4,
    },
    passwordGridItem: {
        width: 12,
    },
    cancelGridItem: {
        width: 4,
        css: css`
            flex-direction: column;
            align-items: center;
            font-weight: bold;
            justify-content: center;
            span:hover {
                cursor: pointer;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                             {
                                margin: 0 auto !important;
                                order: 12;
                            }
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    cancelText: {
        css: css`
            border-bottom: 2px solid ${themeColors.colorInfo};
            line-height: 1;
            font-size: 0.9rem;
            color: ${themeColors.colorInfo};
        `,
    },
    confirmPasswordGridItem: {
        width: 12,
    },
    subscriptionGridItem: {
        width: 12,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: {
        width: 12,
        css: css`
            &:empty {
                display: none;
            }
        `,
    },
    submitGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            justify-content: flex-end;
        `,
    },
    requirementsGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 3.125rem;
                        `,
                    ],
                    "min",
                )}/* stylelint-disable-line declaration-block-single-line-max-declarations */
        `,
    },
    passwordRequirementsGridContainer: {
        gap: 5,
    },
    passwordRequirementsGridItem: {
        width: 12,
    },
    requirementsTitle: {
        variant: "h4",
        as: "h2",
    },
    validatePassword: {
        css: css`
            color: green;
            margin-right: 0.5rem;
        `,
    },

    forTitle: {
        css: css`
            font-size: 1.5rem;
            color: ${themeColors.colorPrimary};
            font-weight: 500;
            letter-spacing: -0.02em;
            width: 100%;
            white-space: nowrap;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 1.2rem;
                    `,
                    css`
                        font-size: 1.3rem;
                    `,
                    css`
                        font-size: 1.5rem;
                    `,
                    css`
                        font-size: 1.5rem;
                    `,
                    css`
                        font-size: 1.5rem;
                    `,
                ])}
        `,
    },
    forSubtitle: {
        css: css`
            font-size: 1.2rem;
            color: ${themeColors.colorPrimary};
            font-weight: 400;
            letter-spacing: -0.02em;
            width: 100%;
            border-bottom: 1px solid rgba(194, 209, 217, 0.45);
            line-height: 3rem;
        `,
    },
    redUnderline: {
        css: css`
            height: 0.1875rem;
            width: 2.5rem;
            background-color: ${themeColors.colorDanger};
        `,
    },
    flexcolumn: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-basis: 50%;
        `,
    },
    flexrow: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-basis: 50%;
            align-items: center;
        `,
    },
    greyUnderline: {
        css: css`
            height: 0.0625rem;
            background-color: rgba(194, 209, 217, 0.45);
            padding: 0 0.9375rem;
            margin-top: 1rem;
            width: 100%;
        `,
    },
    wFull: {
        css: css`
            width: 100%;
            max-width: 100%;
            flex-basis: 100%;
        `,
    },
    wHalf: {
        css: css`
            padding-top: 7.5rem;
            padding-left: 2.5rem;
            position: relative;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        flex-basis: 50%;
                        max-width: 50%;
                    `,
                    css`
                        flex-basis: 50%;
                        max-width: 50%;
                    `,
                    css`
                        flex-basis: 50%;
                        max-width: 50%;
                    `,
                ])}
        `,
    },

    mr1: {
        css: css`
            margin-right: 0.5rem;
            white-space: nowrap;
            font-size: 0.9rem;
        `,
    },
    forCard: {
        css: css`
            padding: 2rem 2rem 3rem;
            box-shadow: -1px 1px 5px 4px rgba(0, 0, 0, 0.04);
            -webkit-box-shadow: -1px 1px 5px 4px rgba(0, 0, 0, 0.04);
            -moz-box-shadow: -1px 1px 5px 4px rgba(0, 0, 0, 0.04);
            background-color: ${themeColors.colorWhite};
            width: 100%;
            max-width: 100%;
            flex-direction: column;
            align-self: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                    `,
                    css`
                        flex-basis: 80%;
                    `,
                    css`
                        flex-basis: 80%;
                    `,
                    css`
                        flex-basis: 80%;
                    `,
                ])}
        `,
    },

    mb3: {
        css: css`
            margin-bottom: 2rem;
        `,
    },
    mb6: {
        css: css`
            margin-bottom: 6rem;
        `,
    },
    blueLink: {
        css: css`
            color: ${themeColors.colorPrimary};
        `,
    },
    saveButton: {
        css: css`
            cursor: pointer;
            font-family: inherit;
            min-height: 2.5rem;
            line-height: 1;
            -webkit-backface-visibility: hidden;
            backface-visibility: hidden;
            -webkit-transition: all 200ms ease-in-out;
            transition: all 200ms ease-in-out;
            word-break: keep-all;
            padding: 0 2rem;
            background: ${themeColors.colorSecondary};
            border: 1px solid ${themeColors.colorSecondary};
            color: ${themeColors.colorPrimary};
            -webkit-border-radius: 0.125rem;
            -moz-border-radius: 0.125rem;
            border-radius: 0.125rem;
            width: 100%;
            &:hover {
                background: ${themeColors.colorPrimaryButtonHover};
                border: 1px solid ${themeColors.colorPrimaryButtonHover};
                color: ${themeColors.colorPrimary};
            }
            &:active,
            &:focus {
                background: ${themeColors.colorPrimaryButtonActive};
                border: 1px solid ${themeColors.colorPrimaryButtonActive};
                color: ${themeColors.colorPrimary};
            }
            &:disabled {
                background-color: ${themeColors.colorWhite};
                color: ${themeColors.colorDisabled};
                border: 1.5px solid ${themeColors.colorDisabled};
            }
        `,
    },
    aleft: {
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    validationFailed: {
        css: css`
            color: ${themeColors.colorGrey};
        `,
    },
    formStylesSpace: {
        css: css`
            padding: 0.75rem 0.9375rem 0;
        `,
    },

    showMobile: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                        padding: 0;
                        margin-bottom: 1.5rem !important;
                    `,
                    css`
                        display: flex;
                        padding: 0;
                        margin-bottom: 1.5rem !important;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                ])}
        `,
    },

    hideDesk: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    showsmall: {
        css: css`
            align-items: center;
            justify-content: end;
            @media (max-width: 767px) {
                display: none;
            }
        `,
    },
    hidebig: {
        css: css`
            align-items: center;
            justify-content: end;
            @media (min-width: 768px) {
                display: none;
            }
        `,
    },
};

const styles = createAccountStyles;
const StyledForm = getStyledWrapper("form");

const CreateAccount = ({
    addAccount,
    signIn,
    validatePassword,
    validateReCaptcha,
    accountSettings,
    returnUrl,
    siteUrl,
    fields: { newsletterText, newsletterCSS, showNewsletter, newsletterDefault },
}: Props) => {
    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
    const emailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");
    const firstNameRequiredFieldMessage = siteMessage(
        "CreateNewAccountInfo_FirstName_Required",
        "First Name is required.",
    );
    const lastNameRequiredFieldMessage = siteMessage(
        "CreateNewAccountInfo_LastName_Required",
        "Last Name is required.",
    );
    const companyNameRequiredFieldMessage = siteMessage(
        "CreateNewAccountInfo_CompanyName_Required",
        "Company Name is required.",
    );
    const [FirstName, setFirstname] = useState("");
    const [LastName, setLastname] = useState("");
    const [firstNameError, setFirstnameError] = useState<ReactNode>("");
    const [lastNameError, setLastnameError] = useState<ReactNode>("");
    const [Phone, setPhoneNumber] = useState("");
    const [CompanyName, setCompanyName] = useState("");
    const [Ext, setPhoneNumberExtension] = useState("");
    const [companyNameError, setCompanyNameError] = useState<ReactNode>("");
    const [Email, setEmail] = useState("");
    const [UserName, setUserName] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [Password, setPassword] = useState("");
    const [passwordErrorMessage, setPasswordErrorMessage] = useState<ReactNode>("");
    const [ConfirmPassword, setConfirmPassword] = useState("");
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState<ReactNode>("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(newsletterDefault);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailError, setEmailError] = useState<ReactNode>("");
    const toasterContext = useContext(ToasterContext);
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    const validateSubmitEnabled = () => {
        return !(
            emailError ||
            passwordErrorMessage ||
            confirmPasswordErrorMessage ||
            firstNameError ||
            lastNameError ||
            companyNameError
        );
    };

    const {
        passwordMinimumLength,
        passwordRequiresUppercase,
        passwordRequiresSpecialCharacter,
        passwordRequiresLowercase,
        passwordRequiresDigit,
        useEmailAsUserName,
    } = accountSettings;

    const vallidateEmailForAccount = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : validateEmail(email) ? "" : emailFieldMessage;

        setEmailError(errorMessage);
        return !errorMessage;
    };

    const validateFormPassword = (newPassword: string) => {
        const errorMessage = !newPassword ? siteMessage("CreateNewAccountInfo_Password_Required") : "";
        setPasswordErrorMessage(errorMessage);
        return !errorMessage;
    };
    const validateConfirmPassword = (newPassword: string, newConfirmPassword: string) => {
        const errorMessage =
            newPassword !== newConfirmPassword
                ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch")
                : "";
        setConfirmPasswordErrorMessage(errorMessage);
        return !errorMessage;
    };

    const validatePasswordHandler = (newPassword: string) => {
        // validateConfirmPassword(newPassword, ConfirmPassword);
        validatePassword({
            password: newPassword,
            onComplete: errorMessage => {
                setPasswordErrorMessage(errorMessage);
            },
        });
        // setPassword(newPassword);
    };

    const validateFirstName = (firstname: string) => {
        const errorMessage = !firstname ? firstNameRequiredFieldMessage : "";

        setFirstnameError(errorMessage);
        return !errorMessage;
    };
    const validateLastName = (lastname: string) => {
        const errorMessage = !lastname ? lastNameRequiredFieldMessage : "";
        setLastnameError(errorMessage);
        return !errorMessage;
    };

    const validateCompanyName = (name: string) => {
        const errorMessage = !name ? companyNameRequiredFieldMessage : "";
        setCompanyNameError(errorMessage);
        return !errorMessage;
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        toasterContext.addToast({ body: translate("Account created successfully!"), messageType: "success" });
        setTimeout(() => {
            signIn({
                userName: useEmailAsUserName ? Email : UserName,
                password: Password,
                rememberMe: false,
                returnUrl,
            });
        }, 500);
    };
    const onCancelClickHandler = () => {
        setEmail("");
        setFirstname("");
        setLastname("");
        setCompanyName("");
        setPhoneNumber("");
        setPhoneNumberExtension("");
        setPassword("");
        setConfirmPassword("");
        setErrorMessage("");
        setFirstnameError("");
        setLastnameError("");
        setEmailError("");
        setPasswordErrorMessage("");
        setConfirmPasswordErrorMessage("");
        setCompanyNameError("");
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        if (
            !(
                vallidateEmailForAccount(Email) ||
                validateFormPassword(Password) ||
                validateConfirmPassword(Password, ConfirmPassword) ||
                validateFirstName(FirstName) ||
                validateLastName(LastName) ||
                validateCompanyName(CompanyName)
            ) ||
            !(
                vallidateEmailForAccount(Email) &&
                validateFormPassword(Password) &&
                validateConfirmPassword(Password, ConfirmPassword) &&
                validateFirstName(FirstName) &&
                validateLastName(LastName) &&
                validateCompanyName(CompanyName)
            )
        ) {
            return;
        }
        if (!validateSubmitEnabled()) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });
        if (!isReCaptchaValid) {
            setIsSubmitted(false);
            return;
        }

        setIsSubmitting(true);
        const prop = {
            userFirstName: FirstName,
            userLastName: LastName,
            companyName: CompanyName,
            phoneNumer: Phone,
            ext: Ext,
            siteUrl: siteUrl != null ? siteUrl[0] : "",
        };
        addAccount({
            userName: useEmailAsUserName ? Email : UserName,
            email: Email,
            password: Password,
            isSubscribed,
            properties: prop,
            onSuccess: onSuccessHandler,
            onError: error => {
                setErrorMessage(error);
                setIsSubmitting(false);
                // setIsSubmitted(false);
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        } as AddAccountParameter);
    };
    const policyText = siteMessage("Privacy_Policy_Tooltip_Message").toString();
    const learnMoreUrl = siteMessage("Privacy_Policy_Readmore_Url").toString();

    const digitRegExp = /[0-9]/;
    const lowerRegExp = /[a-z]/;
    const upperRegExp = /[A-Z]/;
    const specialRegExp = /([^a-zA-Z\d])+([a-zA-Z\d])+|([a-zA-Z\d])+([^a-zA-Z\d])+/;
    const isSubmitEnabled = validateSubmitEnabled();

    const newsletterCSSWrapper = {
        css: css`
            ${newsletterCSS}
        `,
    };

    return (
        <GridContainer {...styles.mainGridContainer}>
            <GridItem {...styles.userGridItem}>
                <GridContainer {...styles.userGridContainer} {...styles.flexrow} {...styles.mb3}>
                    <div className="justifyBetween">
                        <GridItem {...styles.emailGridItem} {...styles.flexcolumn}>
                            <Typography {...styles.forTitle}>{translate("Create Your Account")}</Typography>
                            <StyledWrapper {...styles.redUnderline}></StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.emailGridItem} {...styles.flexrow} {...styles.aleft} {...styles.hidebig}>
                            <Clickable href={learnMoreUrl}>
                                <Typography {...styles.mr1}>{translate("Privacy Policy")}</Typography>
                            </Clickable>
                        </GridItem>
                        <GridItem {...styles.emailGridItem} {...styles.flexrow} {...styles.aleft} {...styles.showsmall}>
                            <Typography {...styles.mr1}>{translate("Privacy Policy")}</Typography>
                            <Tooltip
                                {...styles.tooltip}
                                text={policyText}
                                iconProps={{ src: alertIcon }}
                                learnMoreUrl={learnMoreUrl}
                            />
                        </GridItem>
                    </div>
                </GridContainer>

                <GridItem {...styles.showMobile}>
                    <GridItem {...styles.forCard}>
                        <Typography {...styles.requirementsTitle} {...styles.forSubtitle}>
                            {translate("Password Requirements")}
                        </Typography>
                        <GridContainer {...styles.passwordRequirementsGridContainer}>
                            <GridItem {...styles.passwordRequirementsGridItem}>
                                {Password && !(Password.length >= passwordMinimumLength) && (
                                    <Icon {...styles.validationFailed} src="X" />
                                )}
                                {Password && Password.length >= passwordMinimumLength && (
                                    <Icon {...styles.validatePassword} src="Check" />
                                )}
                                {translate("Must be at least {0} characters long", passwordMinimumLength.toString())}
                            </GridItem>
                            {passwordRequiresDigit && (
                                <GridItem {...styles.passwordRequirementsGridItem}>
                                    {Password && digitRegExp.exec(Password) && (
                                        <Icon {...styles.validatePassword} src="Check" />
                                    )}
                                    {Password && !digitRegExp.exec(Password) && (
                                        <Icon {...styles.validationFailed} src="X" />
                                    )}
                                    {translate("Include at least one number")}
                                </GridItem>
                            )}
                            {passwordRequiresUppercase && (
                                <GridItem {...styles.passwordRequirementsGridItem}>
                                    {Password && upperRegExp.exec(Password) && (
                                        <Icon {...styles.validatePassword} src="Check" />
                                    )}
                                    {Password && !upperRegExp.exec(Password) && (
                                        <Icon {...styles.validationFailed} src="X" />
                                    )}
                                    {translate("Include at least one uppercase character")}
                                </GridItem>
                            )}
                            {passwordRequiresLowercase && (
                                <GridItem {...styles.passwordRequirementsGridItem}>
                                    {Password && !lowerRegExp.exec(Password) && (
                                        <Icon {...styles.validationFailed} src="X" />
                                    )}
                                    {Password && lowerRegExp.exec(Password) && (
                                        <Icon {...styles.validatePassword} src="Check" />
                                    )}
                                    {translate("Include at least one lowercase character")}
                                </GridItem>
                            )}
                            {passwordRequiresSpecialCharacter && (
                                <GridItem {...styles.passwordRequirementsGridItem}>
                                    {Password && !specialRegExp.exec(Password) && (
                                        <Icon {...styles.validationFailed} src="X" />
                                    )}
                                    {Password && specialRegExp.exec(Password) && (
                                        <Icon {...styles.validatePassword} src="Check" />
                                    )}
                                    {translate("Password must include at least one non alphanumeric character")}
                                </GridItem>
                            )}
                        </GridContainer>
                    </GridItem>
                </GridItem>

                <StyledForm {...styles.form} {...styles.mb3} onSubmit={submitHandler} noValidate>
                    <GridContainer {...styles.userGridContainer}>
                        <GridItem {...styles.emailGridItem} {...styles.formStylesSpace}>
                            <TextField
                                {...styles.userNameTextField}
                                type="email"
                                label={translate("Email Address")}
                                onChange={e => setEmail(e.currentTarget.value)}
                                onFocus={() => setErrorMessage("")}
                                onBlur={e => vallidateEmailForAccount(e.currentTarget.value)}
                                value={Email}
                                data-test-selector="createAccount_email"
                                required
                                error={emailError}
                                style={styles.userNameTextField}
                            />
                        </GridItem>

                        {/* {!useEmailAsUserName && (
                            <GridItem {...styles.userNameGridItem}>
                                <TextField
                                    label={translate("User Name")}
                                    value={UserName}
                                    data-test-selector="createAccount_userName"
                                    onChange={e => userChangeHandler(e.currentTarget.value)}
                                    error={userNameError}
                                    required
                                />
                            </GridItem>
                        )} */}
                        <GridItem {...styles.passwordGridItem} {...styles.formStylesSpace}>
                            <TextField
                                id="password"
                                {...styles.passwordTextField}
                                type={showPassword ? undefined : "password"}
                                label={translate("Password")}
                                value={Password}
                                onChange={e => setPassword(e.currentTarget.value)}
                                onBlur={e => validatePasswordHandler(e.currentTarget.value)}
                                onFocus={() => setErrorMessage("")}
                                error={passwordErrorMessage}
                                iconProps={{ ...styles.passwordIcon, src: showPassword ? EyeOff : Eye }}
                                iconClickableProps={{
                                    onClick: () => {
                                        setShowPassword(!showPassword);
                                    },
                                    type: "button",
                                }}
                                clickableText={Password}
                                data-test-selector="createAccount_password"
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.confirmPasswordGridItem} {...styles.formStylesSpace}>
                            <TextField
                                id="confirm-password"
                                {...styles.confirmPasswordTextField}
                                type={showConfirmPassword ? undefined : "password"}
                                label={translate("Confirm Password")}
                                value={ConfirmPassword}
                                onChange={e => setConfirmPassword(e.currentTarget.value)}
                                onBlur={e => validateConfirmPassword(Password, e.currentTarget.value)}
                                onFocus={() => setErrorMessage("")}
                                error={confirmPasswordErrorMessage}
                                iconProps={{ ...styles.confirmPasswordIcon, src: showConfirmPassword ? EyeOff : Eye }}
                                iconClickableProps={{
                                    onClick: () => {
                                        setShowConfirmPassword(!showConfirmPassword);
                                    },
                                    type: "button",
                                }}
                                clickableText={ConfirmPassword}
                                data-test-selector="createAccount_confirmPassword"
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.userNameGridItem} {...styles.formStylesSpace}>
                            <TextField
                                {...styles.userNameTextField}
                                label={translate("First Name")}
                                value={FirstName}
                                data-test-selector="createAccount_firstname"
                                onChange={e => setFirstname(e.currentTarget.value)}
                                onBlur={e => validateFirstName(e.currentTarget.value)}
                                onFocus={() => setErrorMessage("")}
                                error={firstNameError}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.userNameGridItem} {...styles.formStylesSpace}>
                            <TextField
                                {...styles.userNameTextField}
                                label={translate("Last Name")}
                                value={LastName}
                                data-test-selector="createAccount_lastname"
                                onChange={e => setLastname(e.currentTarget.value)}
                                onBlur={e => validateLastName(e.currentTarget.value)}
                                onFocus={() => setErrorMessage("")}
                                error={lastNameError}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.userNameGridItem} {...styles.formStylesSpace}>
                            <TextField
                                {...styles.userNameTextField}
                                label={translate("Company Name")}
                                value={CompanyName}
                                data-test-selector="createAccount_companyName"
                                onChange={e => setCompanyName(e.currentTarget.value)}
                                onBlur={e => validateCompanyName(e.currentTarget.value)}
                                onFocus={() => setErrorMessage("")}
                                error={companyNameError}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.phoneGridItem} {...styles.formStylesSpace}>
                            <TextField
                                {...styles.userNameTextField}
                                label={translate("Phone Number")}
                                value={Phone}
                                data-test-selector="createAccount_phoneNumber"
                                onChange={e => setPhoneNumber(e.currentTarget.value)}
                                onFocus={() => setErrorMessage("")}
                            />
                        </GridItem>
                        <GridItem {...styles.extGridItem} {...styles.formStylesSpace}>
                            <TextField
                                {...styles.userNameTextField}
                                label={translate("Ext.")}
                                value={Ext}
                                data-test-selector="createAccount_ext"
                                onChange={e => setPhoneNumberExtension(e.currentTarget.value)}
                                onFocus={() => setErrorMessage("")}
                            />
                        </GridItem>
                        <GridItem {...styles.subscriptionGridItem} {...newsletterCSSWrapper}>
                            {showNewsletter && (
                                <CheckboxGroup {...styles.subscriptionCheckboxGroup}>
                                    <Checkbox
                                        uid="subscription"
                                        {...styles.subscriptionCheckbox}
                                        checked={isSubscribed}
                                        onChange={(e, value) => setIsSubscribed(value)}
                                    >
                                        {parse(newsletterText, parserOptions)}
                                    </Checkbox>
                                </CheckboxGroup>
                            )}
                        </GridItem>
                        {errorMessage && (
                            <GridItem data-test-selector="createAccount_ErrorMessage" {...styles.submitErrorGridItem}>
                                <Typography {...styles.submitErrorTitle}>{errorMessage}</Typography>
                            </GridItem>
                        )}
                        <GridItem {...styles.wFull}>
                            <StyledWrapper {...styles.greyUnderline}></StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.reCaptchaGridItem}>
                            <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                        </GridItem>
                        <GridItem {...styles.cancelGridItem}>
                            <Typography {...styles.cancelText} onClick={onCancelClickHandler}>
                                {translate("Cancel")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.submitGridItem}>
                            <Button
                                {...styles.saveButton}
                                type="submit"
                                disabled={isSubmitting || !isSubmitEnabled || errorMessage !== ""}
                                data-test-selector="createAccount_createButton"
                            >
                                {translate("SAVE MY INFORMATION")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </StyledForm>
            </GridItem>
            <GridItem {...styles.wHalf}>
                <GridItem {...styles.forCard}>
                    <Typography {...styles.requirementsTitle} {...styles.forSubtitle}>
                        {translate("Password Requirements")}
                    </Typography>
                    <GridContainer {...styles.passwordRequirementsGridContainer}>
                        <GridItem {...styles.passwordRequirementsGridItem}>
                            {Password && !(Password.length >= passwordMinimumLength) && (
                                <Icon {...styles.validationFailed} src="X" />
                            )}
                            {Password && Password.length >= passwordMinimumLength && (
                                <Icon {...styles.validatePassword} src="Check" />
                            )}
                            {translate("Must be at least {0} characters long", passwordMinimumLength.toString())}
                        </GridItem>
                        {passwordRequiresDigit && (
                            <GridItem {...styles.passwordRequirementsGridItem}>
                                {Password && digitRegExp.exec(Password) && (
                                    <Icon {...styles.validatePassword} src="Check" />
                                )}
                                {Password && !digitRegExp.exec(Password) && (
                                    <Icon {...styles.validationFailed} src="X" />
                                )}
                                {translate("Include at least one number")}
                            </GridItem>
                        )}
                        {passwordRequiresUppercase && (
                            <GridItem {...styles.passwordRequirementsGridItem}>
                                {Password && upperRegExp.exec(Password) && (
                                    <Icon {...styles.validatePassword} src="Check" />
                                )}
                                {Password && !upperRegExp.exec(Password) && (
                                    <Icon {...styles.validationFailed} src="X" />
                                )}
                                {translate("Include at least one uppercase character")}
                            </GridItem>
                        )}
                        {passwordRequiresLowercase && (
                            <GridItem {...styles.passwordRequirementsGridItem}>
                                {Password && !lowerRegExp.exec(Password) && (
                                    <Icon {...styles.validationFailed} src="X" />
                                )}
                                {Password && lowerRegExp.exec(Password) && (
                                    <Icon {...styles.validatePassword} src="Check" />
                                )}
                                {translate("Include at least one lowercase character")}
                            </GridItem>
                        )}
                        {passwordRequiresSpecialCharacter && (
                            <GridItem {...styles.passwordRequirementsGridItem}>
                                {Password && !specialRegExp.exec(Password) && (
                                    <Icon {...styles.validationFailed} src="X" />
                                )}
                                {Password && specialRegExp.exec(Password) && (
                                    <Icon {...styles.validatePassword} src="Check" />
                                )}
                                {translate("Password must include at least one non alphanumeric character")}
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CreateAccount),
    definition: {
        allowedContexts: ["CreateAccountPage"],
        group: "Create Account",
        fieldDefinitions: [
            {
                name: fields.newsletterText,
                displayName: "Newsletter Text",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.newsletterCSS,
                displayName: "Newsletter CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: "",
            },
            {
                name: fields.showNewsletter,
                displayName: "Show Newsletter",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.newsletterDefault,
                displayName: "Newletter Default Checkbox",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
